/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { Tool } from '@/providers/llm-provider'
import { commitAndPush, type FileChange } from '@/lib/git-operations'
import { createGitHubRepo, initializeAndPushRepo } from '@/lib/github-repo-create'

/**
 * Git tools for LLM function calling
 * Allows LLMs to commit and push changes to GitHub repositories
 */

export interface GitToolContext {
  repoUrl: string
  branch: string
  files: Record<string, string> // filename -> content
  onConfirmationRequired?: (action: GitToolAction) => Promise<boolean>
  onProgress?: (message: string) => void
}

export interface GitToolAction {
  type: 'commit_and_push' | 'create_repo'
  commitMessage?: string
  files?: FileChange[]
  repoUrl?: string
  branch?: string
  repoName?: string
  repoDescription?: string
  isPrivate?: boolean
}

/**
 * Get git tool definitions for LLM function calling
 */
export function getGitTools(): Tool[] {
  return [
    {
      type: 'function',
      function: {
        name: 'commit_and_push',
        description: 'Commit and push changes to the GitHub repository. ALWAYS use this tool when the user asks to commit, push, or save changes to GitHub. The user will be asked to confirm before the commit is actually pushed.',
        parameters: {
          type: 'object',
          properties: {
            message: {
              type: 'string',
              description: 'A clear, descriptive commit message following conventional commit format (e.g., "feat: add user authentication", "fix: resolve login bug", "docs: update README")',
            },
            files: {
              type: 'array',
              items: {
                type: 'string',
              },
              description: 'Array of file paths to commit. If not specified, all changed files will be committed.',
            },
          },
          required: ['message'],
        },
      },
    },
    {
      type: 'function',
      function: {
        name: 'create_github_repo',
        description: 'Create a new GitHub repository. ALWAYS use this tool when the user asks to create a repository, make a repo, or set up GitHub for their project. The user will be asked to confirm before the repository is created.',
        parameters: {
          type: 'object',
          properties: {
            name: {
              type: 'string',
              description: 'The name of the repository (e.g., "my-awesome-project"). Must be unique in the user\'s GitHub account.',
            },
            description: {
              type: 'string',
              description: 'A short description of the repository (optional)',
            },
            private: {
              type: 'boolean',
              description: 'Whether the repository should be private (default: false)',
              default: false,
            },
            initialize_with_files: {
              type: 'boolean',
              description: 'Whether to push the current project files as the initial commit (default: true)',
              default: true,
            },
          },
          required: ['name'],
        },
      },
    },
  ]
}

/**
 * Execute a git tool call
 * Returns the result message to send back to the LLM
 */
export async function executeGitTool(
  toolName: string,
  args: any,
  context: GitToolContext
): Promise<string> {
  if (toolName === 'commit_and_push') {
    return await executeCommitAndPush(args, context)
  }

  if (toolName === 'create_github_repo') {
    return await executeCreateRepo(args, context)
  }

  throw new Error(`Unknown git tool: ${toolName}`)
}

/**
 * Execute commit_and_push tool
 */
async function executeCommitAndPush(
  args: { message: string; files?: string[] },
  context: GitToolContext
): Promise<string> {
  const { message, files: requestedFiles } = args
  const { repoUrl, branch, files: allFiles, onConfirmationRequired, onProgress } = context

  // Determine which files to commit
  let filesToCommit: FileChange[]

  if (requestedFiles && requestedFiles.length > 0) {
    // Commit only specified files
    filesToCommit = requestedFiles
      .filter(path => allFiles[path] !== undefined)
      .map(path => ({
        path,
        content: allFiles[path],
      }))

    if (filesToCommit.length === 0) {
      return `Error: None of the specified files exist in the project. Available files: ${Object.keys(allFiles).join(', ')}`
    }

    if (filesToCommit.length < requestedFiles.length) {
      const missing = requestedFiles.filter(path => !allFiles[path])
      console.warn('[Git Tool] Some requested files not found:', missing)
    }
  } else {
    // Commit all files
    filesToCommit = Object.entries(allFiles).map(([path, content]) => ({
      path,
      content,
    }))
  }

  // Create the action for confirmation
  const action: GitToolAction = {
    type: 'commit_and_push',
    commitMessage: message,
    files: filesToCommit,
    repoUrl,
    branch,
  }

  // Request user confirmation
  if (onConfirmationRequired) {
    const confirmed = await onConfirmationRequired(action)
    if (!confirmed) {
      return `Commit cancelled by user. The changes were not pushed to GitHub.`
    }
  }

  // Execute the commit
  onProgress?.('Committing and pushing changes...')

  const result = await commitAndPush(
    repoUrl,
    branch,
    message,
    filesToCommit,
    onProgress
  )

  if (result.success) {
    return `Successfully committed and pushed ${filesToCommit.length} file(s) to ${branch}.\nCommit hash: ${result.commitHash}\nFiles: ${filesToCommit.map(f => f.path).join(', ')}`
  } else {
    return `Failed to commit and push: ${result.message}`
  }
}

/**
 * Execute create_github_repo tool
 */
async function executeCreateRepo(
  args: { name: string; description?: string; private?: boolean; initialize_with_files?: boolean },
  context: GitToolContext
): Promise<string> {
  const { name, description, private: isPrivate, initialize_with_files } = args
  const { files: allFiles, onConfirmationRequired, onProgress } = context

  // Create the action for confirmation
  const action: GitToolAction = {
    type: 'create_repo',
    repoName: name,
    repoDescription: description,
    isPrivate: isPrivate ?? false,
  }

  // Request user confirmation
  if (onConfirmationRequired) {
    const confirmed = await onConfirmationRequired(action)
    if (!confirmed) {
      return `Repository creation cancelled by user.`
    }
  }

  // Create the repository
  onProgress?.('Creating GitHub repository...')

  const createResult = await createGitHubRepo({
    name,
    description,
    private: isPrivate,
    autoInit: false, // We'll push our own files
  })

  if (!createResult.success) {
    return `Failed to create repository: ${createResult.error}`
  }

  // If initialize_with_files is true, push the current project files
  if (initialize_with_files !== false && createResult.repoUrl) {
    onProgress?.('Initializing repository with project files...')

    const initResult = await initializeAndPushRepo(
      createResult.repoUrl,
      'main',
      allFiles,
      onProgress
    )

    if (initResult.success) {
      return `Successfully created repository "${name}" and pushed ${Object.keys(allFiles).length} file(s).\n\nRepository URL: ${createResult.repoUrl}\n\nYou can now continue working with this repository.`
    } else {
      return `Repository "${name}" was created, but failed to push initial files: ${initResult.error}\n\nRepository URL: ${createResult.repoUrl}\n\nYou can manually push files later.`
    }
  }

  return `Successfully created repository "${name}".\n\nRepository URL: ${createResult.repoUrl}\n\nThe repository is empty. You can push files to it later.`
}

/**
 * Check if git tools are available for the current project
 */
export function canUseGitTools(project: { github?: { url: string; branch: string } } | null): boolean {
  return !!(project?.github?.url && project?.github?.branch)
}
